import { motion, AnimatePresence } from "framer-motion";
import { useMediaQuery } from "@/hooks/use-media-query";

/**
 * Today's progress indicator — TWO modes based on `daily_target`.
 *
 * ── DISCRETE mode  (target ≤ 12)  ──
 *   Renders exactly `target` base dots up-front.
 *   base_dot[i] is filled when i < min(count, target). Each dot's key
 *   includes its filled state so React remounts it on the ±1 that flips
 *   it — giving an individual pop / fade animation on the exact dot
 *   that changed.
 *
 *   OVERFLOW → when count > target, every extra click APPENDS a "bonus"
 *   dot to the same row. Bonus dots are visually distinct: a gold fill
 *   + white outer ring + brighter halo. This makes going beyond the
 *   daily target feel celebratory rather than clipped. The −1 button
 *   naturally removes the last bonus dot first (because count
 *   decrements from the top).
 *
 *   ROW CAP → if `target + overflow` would exceed MAX_ROW, we render
 *   all base dots, only the bonus dots that fit, and a compact "+N"
 *   chip that carries the remainder. This is the single-row rule the
 *   product brief calls out.
 *
 * ── PROGRESSIVE mode  (target > 12)  ──
 *   Nothing is pre-rendered empty. Every +1 APPENDS a fresh dot (up to
 *   MAX_VISIBLE_DOTS visible in a single row), so the row visibly grows
 *   with each click. Dots at positions ≥ target render in the gold
 *   "bonus" style. The slim bar caps at 100% (with a gold cast when
 *   overflow is present). Undo removes the last visible dot with an
 *   exit animation.
 *
 * This component intentionally does NOT show the 7-day history — that
 * lives inside the LinkDetail sheet under a "Last 7d" label.
 */
export default function TodayProgress({
  count = 0,
  target = 5,
  nudge = false,
}) {
  const safeCount = Math.max(0, count);
  const safeTarget = Math.max(1, target);
  const met = safeCount >= safeTarget;
  const nudgeCls = nudge && !met ? "nudge-pulse" : "";

  // On very narrow phones (<480 px) we shrink the max visible dots so the
  // row + slim bar + count text still fits in the card content width
  // without wrapping to a second line.
  const isNarrow = useMediaQuery("(max-width: 479px)");
  const maxRow = isNarrow ? 8 : 15;

  if (safeTarget <= 12) {
    return (
      <DiscreteDots
        count={safeCount}
        target={safeTarget}
        nudgeCls={nudgeCls}
        nudge={nudge && !met}
        dotSize={isNarrow && safeTarget > 8 ? 8 : 10}
        maxRow={maxRow}
      />
    );
  }
  return (
    <ProgressiveDots
      count={safeCount}
      target={safeTarget}
      nudgeCls={nudgeCls}
      nudge={nudge && !met}
      maxVisible={maxRow}
      dotSize={isNarrow ? 8 : 10}
    />
  );
}

/* ------------------------------------------------------------------ */
/* Bonus-dot style (shared between modes) — gold fill + ring + halo.  */
/* Kept as inline style so it doesn't depend on the active theme.     */
/* ------------------------------------------------------------------ */
const BONUS_STYLE = {
  background:
    "radial-gradient(circle at 32% 30%, #fff4c2 0%, #f7c948 55%, #e0a52a 100%)",
  boxShadow:
    "0 0 12px rgba(250, 204, 21, 0.85), 0 0 3px rgba(255, 255, 255, 0.9) inset",
  outline: "1px solid rgba(255, 244, 194, 0.9)",
  outlineOffset: "0px",
};

/* ------------------------------------------------------------------ */
/* Discrete mode                                                       */
/* ------------------------------------------------------------------ */
function DiscreteDots({ count, target, nudgeCls, nudge, dotSize = 10, maxRow }) {
  const baseFilled = Math.min(count, target);
  const overflow = Math.max(0, count - target);

  // Bonus dots fit as long as (target + shown bonus + optional +N chip)
  // stays under `maxRow`. If we can render every bonus dot outright, we
  // don't need the chip at all.
  const canFitAllBonus = target + overflow <= maxRow;
  const bonusRoomWithChip = Math.max(0, maxRow - target - 1); // leave room for chip
  const bonusToRender = canFitAllBonus ? overflow : bonusRoomWithChip;
  const hiddenBonus = overflow - bonusToRender;

  return (
    <div
      className={"inline-flex items-center rounded-md " + nudgeCls}
      style={{ gap: "4px", padding: nudge ? "2px" : 0 }}
      data-testid="today-progress"
      data-mode="discrete"
      data-count={count}
      data-target={target}
      data-overflow={overflow}
      data-bonus-rendered={bonusToRender}
      data-nudge={nudge ? "true" : "false"}
      aria-label={`${count} of ${target} clicks today`}
    >
      {/* Base dots — one per unit of `target` */}
      {Array.from({ length: target }, (_, i) => {
        const filled = i < baseFilled;
        return (
          <motion.span
            key={`base-${i}-${filled ? "on" : "off"}`}
            data-testid="progress-dot"
            data-kind="base"
            data-index={i}
            data-filled={filled ? "true" : "false"}
            initial={
              filled
                ? { scale: 0.2, opacity: 0.35 }
                : { scale: 1.15, opacity: 0.75 }
            }
            animate={
              filled
                ? { scale: 1, opacity: 1 }
                : { scale: 0.88, opacity: 0.5 }
            }
            transition={
              filled
                ? { type: "spring", stiffness: 620, damping: 13, mass: 0.45 }
                : { type: "tween", duration: 0.24, ease: [0.4, 0, 0.2, 1] }
            }
            className={
              "shrink-0 rounded-full " + (filled ? "bg-accent" : "bg-white/10")
            }
            style={{
              width: `${dotSize}px`,
              height: `${dotSize}px`,
              boxShadow: filled
                ? "0 0 8px rgb(var(--accent) / 0.65)"
                : "inset 0 0 0 1px rgb(255 255 255 / 0.04)",
              transition:
                "background-color 300ms ease, box-shadow 300ms ease",
            }}
          />
        );
      })}

      {/* Bonus dots — gold, celebratory. AnimatePresence so each new dot
          pops in and each removed dot exit-animates on undo. */}
      <AnimatePresence initial={false}>
        {Array.from({ length: bonusToRender }, (_, i) => (
          <motion.span
            key={`bonus-${i}`}
            data-testid="progress-dot"
            data-kind="bonus"
            data-index={target + i}
            data-filled="true"
            initial={{ scale: 0.15, opacity: 0.35, rotate: -25 }}
            animate={{ scale: 1, opacity: 1, rotate: 0 }}
            exit={{ scale: 0, opacity: 0 }}
            transition={{
              type: "spring",
              stiffness: 560,
              damping: 12,
              mass: 0.5,
            }}
            className="shrink-0 rounded-full"
            style={{
              width: `${dotSize + 1}px`,
              height: `${dotSize + 1}px`,
              ...BONUS_STYLE,
            }}
          />
        ))}
      </AnimatePresence>

      {/* +N chip — the "compact indicator" for whatever bonus we
          couldn't fit in the row (e.g. 18/5 with target=5 and cap=15
          might render as [5 base] [9 bonus] [+4]). */}
      <AnimatePresence>
        {hiddenBonus > 0 && (
          <motion.span
            key={`chip-${hiddenBonus}`}
            data-testid="progress-overflow-chip"
            data-hidden={hiddenBonus}
            initial={{ opacity: 0, scale: 0.6, x: -4 }}
            animate={{ opacity: 1, scale: 1, x: 0 }}
            exit={{ opacity: 0, scale: 0.6 }}
            transition={{ type: "spring", stiffness: 420, damping: 20 }}
            className="ml-1 inline-flex items-center rounded-full px-1.5 py-0.5 font-mono-num text-[10px] font-bold leading-none"
            style={{
              background:
                "linear-gradient(135deg, rgba(250, 204, 21, 0.28), rgba(250, 204, 21, 0.14))",
              border: "1px solid rgba(250, 204, 21, 0.55)",
              color: "#fde68a",
              boxShadow: "0 0 10px rgba(250, 204, 21, 0.35)",
            }}
          >
            +{hiddenBonus}
          </motion.span>
        )}
      </AnimatePresence>
    </div>
  );
}

/* ------------------------------------------------------------------ */
/* Progressive mode                                                    */
/* ------------------------------------------------------------------ */

function ProgressiveDots({
  count,
  target,
  nudgeCls,
  nudge,
  maxVisible = 15,
  dotSize = 10,
}) {
  const overflow = Math.max(0, count - target);
  const visibleDotCount = Math.min(count, maxVisible);
  const pct = Math.min(100, Math.round((count / target) * 100));
  const met = count >= target;

  return (
    <div
      className={"inline-flex items-center gap-2 rounded-md " + nudgeCls}
      style={{ padding: nudge ? "2px" : 0 }}
      data-testid="today-progress"
      data-mode="progressive"
      data-count={count}
      data-target={target}
      data-overflow={overflow}
      data-visible-dots={visibleDotCount}
      data-nudge={nudge ? "true" : "false"}
      aria-label={`${count} of ${target} clicks today`}
    >
      <div className="inline-flex items-center gap-1">
        <AnimatePresence initial={false}>
          {Array.from({ length: visibleDotCount }, (_, i) => {
            const isBonus = i >= target; // renders gold once beyond target
            return (
              <motion.span
                key={`p-${i}`}
                data-testid="progress-dot"
                data-kind={isBonus ? "bonus" : "base"}
                data-index={i}
                data-filled="true"
                initial={{ scale: 0.2, opacity: 0.35 }}
                animate={{ scale: 1, opacity: 1 }}
                exit={{ scale: 0, opacity: 0 }}
                transition={{
                  type: "spring",
                  stiffness: 620,
                  damping: 13,
                  mass: 0.45,
                }}
                className={
                  "shrink-0 rounded-full " + (isBonus ? "" : "bg-accent")
                }
                style={
                  isBonus
                    ? {
                        width: `${dotSize + 1}px`,
                        height: `${dotSize + 1}px`,
                        ...BONUS_STYLE,
                      }
                    : {
                        width: `${dotSize}px`,
                        height: `${dotSize}px`,
                        boxShadow: "0 0 8px rgb(var(--accent) / 0.65)",
                        transition:
                          "background-color 300ms ease, box-shadow 300ms ease",
                      }
                }
              />
            );
          })}
        </AnimatePresence>
      </div>

      <div
        className="relative h-1 w-10 shrink-0 overflow-hidden rounded-full bg-white/[0.08] sm:w-14"
        data-testid="progress-slim-bar"
      >
        <motion.div
          className="h-full rounded-full"
          initial={false}
          animate={{ width: `${pct}%` }}
          transition={{ type: "spring", stiffness: 160, damping: 22, mass: 0.6 }}
          style={{
            background:
              overflow > 0
                ? "linear-gradient(90deg, rgb(var(--accent)), #facc15)"
                : "rgb(var(--accent))",
            boxShadow:
              overflow > 0
                ? "0 0 10px rgba(250, 204, 21, 0.7)"
                : "0 0 8px rgb(var(--accent) / 0.55)",
          }}
        />
      </div>

      <span
        className="font-mono-safe text-[11px] leading-none text-text-muted"
        data-testid="progress-count-text"
      >
        <span className={met ? "text-accent" : "text-text-main"}>{count}</span>
        <span className="text-text-muted/70">/{target}</span>
      </span>
    </div>
  );
}
